//Helpers
import { getSystemStyle, hexToRgba } from './chart_helper';
import { SystemStyles } from '@Shelly/shelly-design-system';
import { AvatarColorVariant } from '@Shelly/shelly-design-system/helpers/enums';
//Types
import type { BarElement, ScriptableContext } from 'chart.js';
import { convertDashToUnderscore } from 'src/helpers/utilities';

export type ChartGradientColorType = {
	start: string;
	end: string;
	solid: string;
	labelIndicatorGradientColor?: boolean;
	tooltipIndicatorColor?: string;
	barGradientColor?: boolean;
	gradientOpacity?: { start: number; end: number };
};

//#regionstart Chart helpers for generating gradients
export const createChartBarBackgroundGradient = (
	context: ScriptableContext<'bar' | 'line'>,
	gradientColors: ChartGradientColorType,
) => {
	const { chart } = context;
	const { ctx } = chart;
	const { element } = context as ScriptableContext<'bar'> & {
		element: BarElement & { base: number };
	};

	if (!element || !element.y) return gradientColors.solid;
	if (Math.round(element.y) == Math.round(element.base)) {
		return createChartLineBackgroundGradientFill(context, gradientColors);
	}

	const elementTop = Math.round(element.y);
	const elementBottom = Math.round(element.base);
	const gradient = ctx.createLinearGradient(0, elementBottom, 0, elementTop);
	gradient.addColorStop(0, gradientColors.start);
	gradient.addColorStop(1, gradientColors.end);

	return gradient;
};

export const createChartLineBackgroundGradientFill = (
	context: ScriptableContext<'line' | 'bar'>,
	gradientColors: ChartGradientColorType,
	top: number = null,
	bottom: number = null,
) => {
	const chart = context.chart;
	const { ctx, chartArea, scales } = chart;

	if (!chartArea) return;
	if (top == null || bottom == null || isNaN(top) || isNaN(bottom)) {
		top = chartArea.top;
		bottom = chartArea.bottom;
	}

	const gradientBG = ctx.createLinearGradient(0, bottom, 0, top);
	if (gradientColors.gradientOpacity != null) {
		gradientBG.addColorStop(
			0,
			hexToRgba(gradientColors.start, gradientColors.gradientOpacity.start),
		);
		gradientBG.addColorStop(1, hexToRgba(gradientColors.end, gradientColors.gradientOpacity.end));
	} else {
		gradientBG.addColorStop(0, gradientColors.start);
		gradientBG.addColorStop(1, gradientColors.end);
	}

	return gradientBG;
};

export const createChartLineBackgroundGradient = (
	context: ScriptableContext<'line' | 'bar'>,
	gradientColors: ChartGradientColorType,
) => {
	const { chartArea, scales } = context.chart;

	if (!chartArea) return;
	if (!scales?.y?.getPixelForValue) {
		return createChartLineBackgroundGradientFill(context, gradientColors);
	}

	let bottom: number = null;
	let top: number = null;
	const minY: number = scales.y.min;
	const maxY: number = scales.y.max;
	const minValue: number = Math.min(...(context.dataset.data as number[]));
	const maxValue: number = Math.max(...(context.dataset.data as number[]));

	if (maxValue <= 0 && maxY > 0) {
		bottom = minY;
		top = minY < 0 ? 0 : maxValue;
	} else if (minY <= 0 && maxY <= 0) {
		bottom = minValue;
		top = maxY;
	} else {
		bottom = minY < 0 && maxY > 0 ? 0 : minY;
		top = maxValue;
	}

	bottom = scales.y.getPixelForValue(bottom);
	top = scales.y.getPixelForValue(top);

	return createChartLineBackgroundGradientFill(context, gradientColors, top, bottom);
};

export const createChartLabelGradient = (
	context: ScriptableContext<'bar'>,
	gradientColors: ChartGradientColorType | string,
) => {
	if (typeof gradientColors == 'string') return gradientColors;
	const { chart } = context;
	const { ctx, chartArea } = chart;

	if (!chartArea) return;

	const startHeight = chart.scales.x.bottom + 8;
	const endHeight = startHeight + 8;

	const gradient = ctx.createLinearGradient(0, endHeight, 0, startHeight);
	gradient.addColorStop(0, gradientColors.start);
	gradient.addColorStop(1, gradientColors.end);

	return gradient;
};
//#regionend Chart helpers for generating gradients

export const getChartColorStyle = (
	context: ScriptableContext<'bar' | 'line'>,
	gradientColor: ChartGradientColorType,
): CanvasGradient | string => {
	if (!gradientColor.start) {
		return gradientColor.solid;
	}
	const currentChartType = context.chart.config['_config'].type;
	if (currentChartType == 'bar') {
		return createChartBarBackgroundGradient(context, gradientColor);
	}
	return createChartLineBackgroundGradient(context, gradientColor);
};

//#regionstart Chart colors helpers list
export const getGradientColor = (style: string, chartType: string): ChartGradientColorType => {
	switch (style) {
		case 'consumption':
		case 'dynamicChart_current':
		case 'overall_consumption':
		case 'grid_source':
		case 'monthly_report_consumption':
		case 'max_distance':
			if (chartType == 'bar') {
				return {
					start: getSystemStyle(SystemStyles.GRADIENT_CHART_CONSUMED_ENERGY_START),
					end: getSystemStyle(SystemStyles.GRADIENT_CHART_CONSUMED_ENERGY_END),
					solid: getSystemStyle(SystemStyles.COLOR_CHART_CONSUMED_ENERGY),
					tooltipIndicatorColor: getSystemStyle(SystemStyles.GRADIENT_CHART_CONSUMED_ENERGY),
					labelIndicatorGradientColor: true,
					barGradientColor: true,
				};
			}
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_CONSUMED_ENERGY_START),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_CONSUMED_ENERGY_END),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_CONSUMED_ENERGY),
				tooltipIndicatorColor:
					chartType.startsWith('area') &&
					getSystemStyle(SystemStyles.GRADIENT_CHART_CONSUMED_ENERGY),
				labelIndicatorGradientColor: chartType.startsWith('area'),
				gradientOpacity: { start: 0.4, end: 0.4 },
			};
		case 'avg_distance':
		case 'generated_energy':
		case 'dynamicChart_excluded':
			if (chartType == 'bar') {
				return {
					start: getSystemStyle(SystemStyles.GRADIENT_CHART_EXCLUDED_ENERGY_START),
					end: getSystemStyle(SystemStyles.COLOR_CHART_EXCLUDED_ENERGY),
					solid: getSystemStyle(SystemStyles.COLOR_CHART_EXCLUDED_ENERGY),
					barGradientColor: true,
				};
			}
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_EXCLUDED_ENERGY_START),
				end: getSystemStyle(SystemStyles.COLOR_CHART_EXCLUDED_ENERGY),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_EXCLUDED_ENERGY),
			};
		case 'total_house_energy':
		case 'prev_period_reversed':
		case 'freq_measurement_avg':
		case 'tariff_intervals_utc':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_PREV_GENERATED_ENERGY_END),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_PREV_GENERATED_ENERGY_START),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_PREV_GENERATED_ENERGY),
			};
		case 'prev_period_consumption':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_EXCLUDED_ENERGY_START),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_EXCLUDED_ENERGY_END),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_EXCLUDED_ENERGY),
			};
		case 'reversed':
		case 'returned':
		case 'overall_reversed':
		case 'monthly_report_reversed':
			if (chartType == 'bar') {
				return {
					start: getSystemStyle(SystemStyles.GRADIENT_CHART_GENERATED_ENERGY_START),
					end: getSystemStyle(SystemStyles.COLOR_CHART_GENERATED_ENERGY),
					solid: getSystemStyle(SystemStyles.COLOR_CHART_GENERATED_ENERGY),
					barGradientColor: true,
				};
			}
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_GENERATED_ENERGY_END),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_GENERATED_ENERGY_START),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_GENERATED_ENERGY),
			};
		case 'min_temperature':
		case 'tempSchedule_cool':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_MIN_TEMPERATURE_START),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_MIN_TEMPERATURE_END),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_MIN_TEMPERATURE),
			};
		case 'max_temperature':
		case 'tempSchedule_heat':
		case 'negative_energy':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_MAX_TEMPERATURE_START),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_MAX_TEMPERATURE_END),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_MAX_TEMPERATURE),
			};
		case 'humidity':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_HUMIDITY_START),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_HUMIDITY_END),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_HUMIDITY),
			};
		case 'phase_a':
		case 'min_distance':
		case 'phase_a_min_voltage':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_PHASE_A_START),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_PHASE_A_END),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_PHASE_A),
			};
		case 'phase_b':
		case 'phase_b_min_voltage':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_PHASE_B_START),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_PHASE_B_END),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_PHASE_B),
			};
		case 'phase_c':
		case 'phase_c_min_voltage':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_PHASE_C_START),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_PHASE_C_END),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_PHASE_C),
			};
		case 'total_consumption':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_PHASE_TOTAL_START),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_PHASE_TOTAL_END),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_PHASE_TOTAL),
			};
		case 'phase_a_returned':
		case 'phase_a_max_voltage':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_PHASE_A_RET_END),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_PHASE_A_RET_START),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_PHASE_A_RET),
			};
		case 'phase_b_returned':
		case 'phase_b_max_voltage':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_PHASE_B_RET_END),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_PHASE_B_RET_START),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_PHASE_B_RET),
			};
		case 'phase_c_returned':
		case 'phase_c_max_voltage':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_PHASE_C_RET_END),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_PHASE_C_RET_START),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_PHASE_C_RET),
			};
		case 'total_returned':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_PHASE_TOTAL_RET_END),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_PHASE_TOTAL_RET_START),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_PHASE_TOTAL_RET),
			};
		case 'min_voltage':
		case 'voltage':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_MIN_VOLTAGE_START),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_MIN_VOLTAGE_END),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_MIN_VOLTAGE),
			};
		case 'max_voltage':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_MAX_VOLTAGE_START),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_MAX_VOLTAGE_END),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_MAX_VOLTAGE),
			};
		case 'min_input':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_MIN_INPUT_START),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_MIN_INPUT_END),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_MIN_INPUT),
			};
		case 'max_input':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_MAX_INPUT_START),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_MAX_INPUT_END),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_MAX_INPUT),
			};
		case 'valve_position':
		case 'freq_measurement_min':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_TRV_START),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_TRV_END),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_TRV),
			};
		case 'tariff_intervals':
			return {
				start: getSystemStyle(SystemStyles.CHART_MUTED_START),
				end: getSystemStyle(SystemStyles.CHART_MUTED_END),
				solid: getSystemStyle(SystemStyles.COLOR_MUTED),
			};
		case 'ppm':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_PPM_START),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_PPM_END),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_PPM),
			};
		case 'counter_accumulation':
		case 'freq_measurement_max':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_PPM_START),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_PPM_END),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_PPM),
			};
	}
	console.log("missing chart's style: " + style);
	return {
		start: getSystemStyle(SystemStyles.GRADIENT_CHART_HUMIDITY_START),
		end: getSystemStyle(SystemStyles.GRADIENT_CHART_HUMIDITY_END),
		solid: getSystemStyle(SystemStyles.COLOR_CHART_HUMIDITY),
	};
};

export const getIntervalAvatarColors = (
	colorKey,
): {
	start: string;
	end: string;
	solid: string;
} => {
	const colorValues = Object.values(AvatarColorVariant).slice(0, -13);
	const currentColor = colorValues.find((color) => color === colorKey);
	if (!currentColor) return colorKey;
	const colorValue = convertDashToUnderscore(currentColor?.split('color-')[1].toUpperCase());

	if (colorValue) {
		return {
			start: getSystemStyle(SystemStyles[`GRADIENT_${colorValue}_CHART_START`]),
			end: getSystemStyle(SystemStyles[`GRADIENT_${colorValue}_CHART_END`]),
			solid: getSystemStyle(
				SystemStyles[`${convertDashToUnderscore(currentColor).toUpperCase()}`],
			),
		};
	}
	return colorKey;
};

//#regionend Chart colors helpers list
